import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";
import { GradientText, SectionLabel } from "./GradientText";
import {
  Users,
  Zap,
  BarChart3,
  Link2,
  Shield,
  Headphones,
  Globe,
  type LucideIcon,
} from "lucide-react";

interface Feature {
  id: string;
  title?: string;
  description?: string;
  icon: string;
}

interface FeaturesProps {
  features: Feature[];
}

const iconMap: Record<string, LucideIcon> = {
  users: Users,
  zap: Zap,
  chart: BarChart3,
  link: Link2,
  shield: Shield,
  headphones: Headphones,
  globe: Globe,
};

/**
 * Features
 *
 * Asymmetric bento grid layout with:
 * - Variable card sizes for visual interest
 * - Mouse-tracking spotlight effect on each card
 * - Icon containers
 * - Text from translations keyed by feature id
 */
export function Features({ features }: FeaturesProps) {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const heading = t("productDetail.heading");
  const descriptionParagraphs = t("productDetail.description", {
    returnObjects: true,
  }) as string[];

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Define bento grid layout patterns
  const getGridClasses = (index: number) => {
    const patterns = [
      "md:col-span-2 md:row-span-2", // Hero card - large
      "md:col-span-1 md:row-span-1", // Standard
      "md:col-span-1 md:row-span-1", // Standard
      "md:col-span-1 md:row-span-2", // Tall card
      "md:col-span-2 md:row-span-1", // Wide card
      "md:col-span-1 md:row-span-1", // Standard
      "md:col-span-1 md:row-span-1", // Standard
    ];
    return patterns[index % patterns.length];
  };

  return (
    <section ref={sectionRef} id="features" className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "mx-auto mb-16 max-w-3xl text-center lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <SectionLabel className="mb-6">Features</SectionLabel>

          {heading && (
            <h2
              className={cn(
                "text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl",
                "mb-6",
              )}
            >
              <GradientText>{heading}</GradientText>
            </h2>
          )}

          {Array.isArray(descriptionParagraphs) &&
            descriptionParagraphs.map((paragraph, i) => (
              <p
                key={i}
                className="mb-4 text-base leading-relaxed text-muted-foreground last:mb-0 lg:text-lg"
              >
                {paragraph}
              </p>
            ))}
        </div>

        {/* Bento grid */}
        <div className="grid auto-rows-[180px] grid-cols-1 gap-4 md:grid-cols-3 lg:gap-6">
          {features.map((feature, index) => {
            const Icon = iconMap[feature.icon] || Zap;
            const isHeroCard = index === 0;
            const featureTitle = t(`features.${feature.id}.title`, {
              defaultValue: feature.title,
            });
            const featureDescription = t(`features.${feature.id}.description`, {
              defaultValue: feature.description,
            });

            return (
              <SpotlightCard
                key={feature.id}
                className={cn(
                  getGridClasses(index),
                  "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
                )}
                style={{
                  transitionDelay: isVisible ? `${index * 80}ms` : "0ms",
                }}
              >
                <SpotlightCardContent
                  className={cn(
                    "flex h-full flex-col overflow-hidden",
                    isHeroCard ? "p-6 lg:p-8" : "p-5",
                  )}
                >
                  {/* Icon + Title row */}
                  <div className="mb-3 flex min-w-0 items-center gap-3">
                    {/* Icon */}
                    <div
                      className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                        "bg-primary/10",
                        "border border-primary/20",
                      )}
                    >
                      <Icon className="h-5 w-5 text-primary" />
                    </div>

                    {/* Title */}
                    <h3
                      className={cn(
                        "truncate font-semibold text-foreground",
                        isHeroCard ? "text-lg lg:text-xl" : "text-base",
                      )}
                      title={featureTitle}
                    >
                      {featureTitle}
                    </h3>
                  </div>

                  {/* Description */}
                  <p
                    className={cn(
                      "line-clamp-3 flex-1 leading-relaxed text-muted-foreground",
                      isHeroCard ? "line-clamp-5 text-sm lg:text-base" : "text-sm",
                    )}
                  >
                    {featureDescription}
                  </p>
                </SpotlightCardContent>
              </SpotlightCard>
            );
          })}
        </div>
      </div>
    </section>
  );
}
